import { registerBlock } from "../../registry";
import { makeId } from "../../helpers";
import type { ContentBlock } from "../../types";

const folicAcid: ContentBlock<"medication"> = {
  id: makeId("med-folic-acid"),
  kind: "medication",
  tags: ["medications", "her", "pre-cycle", "evidence-based", "nutritional"],
  title: "Folic acid",
  subtitle: "Vitamin B9 support before conception and in early pregnancy",
  lastReviewed: "2026-08-12",
  reviewedBy: "CDC and ACOG guidance",
  body: {
    genericName: "Folic acid (vitamin B9)",
    drugClass: "Vitamin supplement",
    purpose:
      "Supports new-cell formation and helps reduce the risk of neural tube defects, which develop very early in pregnancy. Folic acid is the form of folate shown to prevent neural tube defects and is found in supplements and fortified foods.",
    commonRoutes: ["oral"],
    commonForms: ["pill", "capsule"],
    dosageRanges:
      "Most people who could become pregnant: 400 mcg daily, starting at least 1 month before pregnancy and continuing through the first 12 weeks. A higher dose may be recommended for specific risk factors; use it only when prescribed or confirmed by your clinician.",
    howToTake:
      "Take the dose on your prenatal vitamin or prescription label once daily, with or without food. Check the full label before adding a separate folic acid product so you do not accidentally double your dose. If you have had a pregnancy affected by a neural tube defect or have another risk factor, ask your clinician which dose and start date are right for you. If you miss a dose, take it when you remember unless it is nearly time for the next dose; do not double up.",
    sideEffects: [
      "Usually well tolerated at recommended amounts",
      "Nausea or stomach upset",
      "Bloating",
      "Bitter or unpleasant taste",
      "Rare allergic reaction",
    ],
    storage:
      "Store at room temperature in the original container, protected from heat and moisture. Keep out of reach of children.",
    brandsByCountry: [
      { country: "US", brands: ["Folic Acid", "Folate", "Prenatal vitamin"] },
      { country: "UK", brands: ["Folic Acid", "Pregnacare"] },
      { country: "EU", brands: ["Acide folique", "Folic acid"] },
      { country: "AU", brands: ["Folic Acid", "Elevit"] },
      { country: "FR", brands: ["Acide folique", "Spéciafoldine"] },
    ],
    aliases: ["Folate", "Acide folique", "Vitamin B9"],
    forWhom: "her",
    frBody: {
      genericName: "Acide folique (vitamine B9)",
      drugClass: "Complément vitaminique",
      purpose:
        "Participe à la formation de nouvelles cellules et contribue à réduire le risque d'anomalies de fermeture du tube neural, qui se développent très tôt pendant la grossesse. L'acide folique est la forme de folate dont l'effet préventif sur ces anomalies a été démontré ; on le trouve dans les compléments et les aliments enrichis.",
      dosageRanges:
        "Pour la plupart des personnes susceptibles d'être enceintes : 400 µg par jour, au moins 1 mois avant la grossesse et jusqu'à la fin des 12 premières semaines. Une dose plus élevée peut être recommandée dans certaines situations ; prenez-la uniquement si elle est prescrite ou confirmée par votre clinicien.",
      howToTake:
        "Prenez chaque jour la dose indiquée sur votre complément prénatal ou votre ordonnance, avec ou sans nourriture. Vérifiez la composition complète avant d'ajouter un autre complément d'acide folique afin d'éviter un double dosage. Si vous avez déjà eu une grossesse concernée par une anomalie du tube neural ou présentez un autre facteur de risque, demandez à votre clinicien quelle dose et quelle date de début vous conviennent. En cas d'oubli, prenez la dose dès que vous vous en souvenez sauf si la suivante est proche ; ne doublez pas la dose.",
      sideEffects: [
        "Généralement bien toléré aux doses recommandées",
        "Nausées ou inconfort gastrique",
        "Ballonnements",
        "Goût amer ou désagréable",
        "Réaction allergique rare",
      ],
      storage:
        "Conservez à température ambiante dans l'emballage d'origine, à l'abri de la chaleur et de l'humidité. Gardez hors de portée des enfants.",
    },
  },
  sources: [
    {
      title: "Folic Acid: Facts for Clinicians",
      authors: "Centers for Disease Control and Prevention",
      year: 2026,
      url: "https://www.cdc.gov/folic-acid/hcp/clinical-overview/index.html",
      evidenceLevel: "guideline",
    },
    {
      title: "Healthy Eating During Pregnancy",
      authors: "American College of Obstetricians and Gynecologists",
      year: 2025,
      url: "https://www.acog.org/womens-health/faqs/healthy-eating-during-pregnancy",
      evidenceLevel: "guideline",
    },
  ],
};

registerBlock(folicAcid);
